import { useEffect } from "react";
import { Phone } from "lucide-react";
import ServiceHeader from "@/components/ServiceHeader";
import Footer from "@/components/Footer";
import StickyCallButton from "@/components/StickyCallButton";

const upsertMeta = (kind: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${kind}="${key}"]`);
  const created = !el;
  const prev = el?.getAttribute("content") ?? "";
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(kind, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return () => {
    if (created) el?.remove();
    else el?.setAttribute("content", prev);
  };
};

const ComingSoonLocation = ({ city, slug }: { city: string; slug: string }) => {
  useEffect(() => {
    const prevTitle = document.title;
    const title = `${city} Removalists - Coming Soon | OZ MOVE CO.`;
    const description = `OZ MOVE CO. is expanding to ${city}. Contact our team to check availability or discuss your upcoming move.`;
    const url = `https://ozmoveco.com.au/${slug}`;
    document.title = title;
    const desc = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content") ?? "";
    desc?.setAttribute("content", description);
    const cleanups = [
      upsertMeta("property", "og:title", title),
      upsertMeta("property", "og:description", description),
      upsertMeta("property", "og:url", url),
      upsertMeta("name", "twitter:title", title),
      upsertMeta("name", "twitter:description", description),
    ];
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute("href") ?? "";
    canonical?.setAttribute("href", url);
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      desc?.setAttribute("content", prevDesc);
      cleanups.forEach((fn) => fn());
      if (canonical) canonical.setAttribute("href", prevCanonical);
    };
  }, [city, slug]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <ServiceHeader />
      <main className="flex-1 bg-[#FFC300] relative overflow-hidden flex items-center">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(255,255,255,0.55),transparent_55%)]" aria-hidden="true" />
        <div className="container relative py-24 md:py-36">
          <div className="max-w-2xl mx-auto text-center bg-white/60 backdrop-blur-xl border border-white/60 shadow-[0_8px_40px_rgba(26,26,26,0.08)] rounded-3xl px-6 py-12 md:px-12 md:py-16">
            <h1 className="text-4xl md:text-5xl font-black text-[#1a1a1a] tracking-tight leading-tight">
              {city} - Coming to Your Area Soon
            </h1>
            <p className="mt-6 text-lg text-[#1a1a1a]/75 leading-relaxed">
              We're expanding across Australia and look forward to servicing your area. Contact our
              team to check current availability or discuss how we may be able to help.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-[#1a1a1a] text-[#FFC300] px-8 py-4 text-lg font-bold rounded-xl hover:opacity-90 transition-opacity"
              >
                Contact Us
              </a>
              <a
                href="tel:1800556654"
                data-call-source="location_page"
                aria-label="Call 1800 556 654"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#1a1a1a] text-[#1a1a1a] px-8 py-4 text-lg font-bold rounded-xl hover:bg-[#1a1a1a]/5 transition-colors"
              >
                <Phone className="w-5 h-5" /> Call Now
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default ComingSoonLocation;
